import React from 'react';
import { Dialog } from '../../components/ui/radix/Dialog';
import { Badge } from '../../components/ui/Badge';
import { Callout } from '../../components/ui/Callout';
import { ImportCandidate } from '../../services/orderImportService';

interface RowDetailDialogProps {
  candidate: ImportCandidate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const addrLine = (a: Record<string, any>): string => {
  const city = a?.city?.name || a?.city || '';
  const country = a?.country?.name || a?.countryCode || '';
  return [a?.street, a?.houseNo, a?.zipCode, city, country].filter(Boolean).join(', ');
};

const AddressBlock: React.FC<{ title: string; a: Record<string, any> }> = ({ title, a }) => (
  <div style={{ flex: 1, minWidth: 220 }}>
    <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
    <div style={{ fontWeight: 600, fontSize: 13 }}>{a?.contactName || a?.companyName || '—'}</div>
    <div style={{ fontSize: 12 }}>{addrLine(a)}</div>
    <div className="muted" style={{ fontSize: 12 }}>{[a?.email, a?.phone].filter(Boolean).join(' · ')}</div>
  </div>
);

export const RowDetailDialog: React.FC<RowDetailDialogProps> = ({ candidate, open, onOpenChange }) => {
  if (!candidate) return null;
  const parcel = candidate.parcels[0];
  const c = candidate.customs;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Row ${candidate.rowNumber} · ${candidate.reference}`}
      maxWidth={760}
    >
      {candidate.errors.length > 0 && (
        <Callout tone="warn" style={{ marginBottom: 10 }}>
          <span>{candidate.errors.join(' ')}</span>
        </Callout>
      )}
      {candidate.warnings.length > 0 && (
        <Callout tone="warn" style={{ marginBottom: 10 }}>
          <span>{candidate.warnings.join(' ')}</span>
        </Callout>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <AddressBlock title="Sender" a={candidate.addresses.from} />
        <AddressBlock title="Recipient" a={candidate.addresses.to} />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Badge>{parcel ? `${parcel.length}×${parcel.width}×${parcel.height} cm` : 'No parcel'}</Badge>
        <Badge>{parcel?.weight ?? 0} kg</Badge>
        {c.exportReason && <Badge>{c.exportReason}</Badge>}
        {c.dutyAndTaxes && <Badge>{c.dutyAndTaxes}</Badge>}
        {c.insuranceAmount > 0 && <Badge>Insured ${c.insuranceAmount}</Badge>}
        {c.vatNumber && <Badge>VAT {c.vatNumber}</Badge>}
      </div>

      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        Items ({parcel?.items.length ?? 0})
      </div>
      <table className="table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th>Weight</th>
            <th>Value</th>
            <th>HS code</th>
            <th>Origin</th>
          </tr>
        </thead>
        <tbody>
          {parcel?.items.map((it, i) => (
            <tr key={i}>
              <td>{it.description}</td>
              <td>{it.quantity}</td>
              <td>{it.weight} kg</td>
              <td>${it.price}</td>
              <td className="mono">{it.commodityCode || '—'}</td>
              <td>{it.countryManufactured || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Dialog>
  );
};

export default RowDetailDialog;
